import "./carousel-embla-DWvH-wV-.js";
import { d as defineCustomElement, f as formatMoney, e as escapeHtml } from "./utilities-BToXpjgA.js";
import "./component-BFVg3Q7H.js";
import "./embla-DjVGOkZN.js";
class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) {
        return;
      }
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: "0px 0px 200px 0px" }
  );
  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== "attributes") {
        continue;
      }
      if (mutation.attributeName === "data-error") {
        continue;
      }
      if (mutation.attributeName === "class" && this.classList.contains("hidden")) {
        continue;
      }
      if (mutation.attributeName === "data-recommendations-performed" && this.dataset.recommendationsPerformed === "true") {
        continue;
      }
      this.#loadRecommendations();
      break;
    }
  });
  /** @type {Map<string, string>} */
  #cache = /* @__PURE__ */ new Map();
  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;
  /** @type {number | undefined} */
  #retryTimer;
  connectedCallback() {
    if (this.dataset.recommendationsProducts && this.dataset.recommendationsJson === "true") {
      try {
        let productsJson = this.dataset.recommendationsProducts;
        let parsedData;
        try {
          parsedData = JSON.parse(productsJson);
        } catch {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = productsJson;
          productsJson = tempDiv.textContent || tempDiv.innerText || productsJson;
          parsedData = JSON.parse(productsJson);
        }
        const products = Array.isArray(parsedData) ? parsedData : parsedData.products || [];
        if (Array.isArray(products) && products.length > 0) {
          this.dataset.recommendationsPerformed = "true";
          this.#renderProductsFromJson(products);
          return;
        }
      } catch (e) {
        console.error("Error parsing existing recommendations products:", e);
      }
    }
    const eagerLoad = this.closest("cart-upsells") !== null;
    const rect = this.getBoundingClientRect();
    const isInViewport = rect.top < window.innerHeight + 400 && // 400px = rootMargin from observer
    rect.bottom > -400 && rect.left < window.innerWidth && rect.right > 0;
    if ((eagerLoad || isInViewport) && this.dataset.recommendationsPerformed !== "true") {
      this.#loadRecommendations();
    } else {
      this.#intersectionObserver.observe(this);
    }
    this.#mutationObserver.observe(this, { attributes: true });
  }
  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    if (this.#retryTimer !== void 0) {
      clearTimeout(this.#retryTimer);
      this.#retryTimer = void 0;
    }
  }
  /**
   * Public method: force-load recommendations immediately (no viewport needed).
   */
  loadRecommendations() {
    this.#loadRecommendations();
  }
  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, manualMode } = this.dataset;
    const id = this.id;
    if (this.#activeFetch !== null) {
      return;
    }
    if (manualMode === "true") {
      this.dataset.recommendationsPerformed = "true";
      return;
    }
    if (!productId || !id) {
      console.warn("Product recommendations: No product ID or element ID");
      this.#hideSection();
      return;
    }
    if (recommendationsPerformed === "true") {
      return;
    }
    this.#fetchCachedRecommendations(productId, sectionId, intent).then((result) => {
      if (!result.success) {
        this.dataset.recommendationsPerformed = "true";
        this.#hideSection();
        if (Shopify.designMode) {
          console.warn(`Product recommendations failed: ${result.status}`);
        }
        return;
      }
      if (result.isJson) {
        try {
          const jsonData = JSON.parse(result.data);
          const products = Array.isArray(jsonData) ? jsonData : jsonData.products || [];
          if (Array.isArray(products)) {
            this.dataset.recommendationsPerformed = "true";
            this.dataset.recommendationsJson = "true";
            this.dataset.recommendationsProducts = JSON.stringify(products);
            const loadingEl = this.querySelector(".cart-upsells-loading");
            if (loadingEl) {
              loadingEl.remove();
            }
            if (products.length === 0) {
              this.dataset.recommendationsPerformed = "true";
              const gridEl = this.querySelector(".grid");
              if (gridEl) {
                gridEl.innerHTML = "";
              }
              this.#hideSection();
              const event2 = new CustomEvent("recommendations-json-loaded", {
                detail: { products: [], productId, sectionId, intent },
                bubbles: true,
                cancelable: true
              });
              this.dispatchEvent(event2);
              return;
            }
            this.#renderProductsFromJson(products);
            const event = new CustomEvent("recommendations-json-loaded", {
              detail: { products, productId, sectionId, intent },
              bubbles: true,
              cancelable: true
            });
            this.dispatchEvent(event);
            return;
          }
        } catch (e) {
          console.error("Error parsing recommendations JSON:", e);
        }
      }
      const html = document.createElement("div");
      html.innerHTML = result.data || "";
      const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
      if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
        this.dataset.recommendationsPerformed = "true";
        this.innerHTML = recommendations.innerHTML;
      } else {
        this.dataset.recommendationsPerformed = "true";
        this.innerHTML = "";
        this.#hideSection();
      }
    }).catch((e) => {
      if (e?.name === "AbortError") {
        if (this.isConnected && this.dataset.recommendationsPerformed !== "true") {
          clearTimeout(this.#retryTimer);
          this.#retryTimer = window.setTimeout(() => {
            if (!this.isConnected) {
              return;
            }
            if (this.dataset.recommendationsPerformed === "true") {
              return;
            }
            try {
              this.#intersectionObserver.observe(this);
            } catch {
            }
            this.#loadRecommendations();
          }, 250);
        }
        return;
      }
      this.#handleError(e);
    });
  }
  /**
   * Hide the section container when no recommendations are available
   */
  #hideSection() {
    const sectionContainer = this.closest('[data-section-type="product-recommendations"]');
    if (sectionContainer) {
      sectionContainer.style.display = "none";
      sectionContainer.setAttribute("aria-hidden", "true");
    }
    const cartUpsells = this.closest(".cart-upsells");
    if (cartUpsells) {
      cartUpsells.style.display = "none";
    }
    const staticUpsells = this.closest("[data-cart-static-upsells]");
    if (staticUpsells) {
      staticUpsells.hidden = true;
      staticUpsells.dataset.upsellsEmpty = "true";
    }
    this.style.display = "none";
    this.classList.add("hidden");
  }
  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const rootPath = window.Shopify?.routes?.root || "/";
    let url = `${rootPath}recommendations/products.json`;
    const params = new URLSearchParams({
      product_id: productId,
      intent: intent || "related",
      limit: this.dataset.maxProducts || "8"
    });
    url = `${url}?${params.toString()}`;
    const cachedResponse = this.#cache.get(url);
    if (cachedResponse) {
      const isJson = url.includes(".json") || cachedResponse.trim().startsWith("[") && cachedResponse.trim().endsWith("]");
      return { success: true, data: cachedResponse, isJson };
    }
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const controller = this.#activeFetch;
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        cache: "no-store",
        credentials: "same-origin"
      });
      if (!response.ok) {
        return { success: false, status: response.status };
      }
      const contentType = response.headers.get("content-type");
      const text = await response.text();
      const trimmedText = text.trim();
      const isJsonResponse = contentType?.includes("application/json") || url.includes(".json") || trimmedText.startsWith("[") && trimmedText.endsWith("]") || trimmedText.startsWith("{") && trimmedText.endsWith("}");
      if (isJsonResponse) {
        this.#cache.set(url, text);
        return { success: true, data: text, isJson: true };
      }
      this.#cache.set(url, text);
      return { success: true, data: text, isJson: false };
    } finally {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
      }
    }
  }
  /**
   * Render products from JSON data
   * @param {Array} products - Array of product objects from JSON API
   */
  #renderProductsFromJson(products) {
    const layoutType = this.dataset.layoutType || "grid";
    const maxProducts = parseInt(this.dataset.maxProducts || "4", 10);
    const limitedProducts = products.slice(0, maxProducts);
    if (layoutType === "carousel") {
      let carousel = this.querySelector("carousel-embla");
      if (!carousel) {
        const placeholderGrid = this.querySelector(".grid");
        if (placeholderGrid) {
          placeholderGrid.remove();
        }
        const carouselHTML = `
          <carousel-embla class="carousel relative block product-recommendations-carousel" data-loop="false">
            <div class="relative carousel-container">
              <div ref="viewport" class="overflow-hidden carousel-viewport">
                <div ref="container" class="flex" style="gap: 1rem;"></div>
              </div>
              <div class="carousel-arrows">
                <button ref="prev" type="button" class="carousel-arrow carousel-arrow--prev" aria-label="Previous"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="m15 18-6-6 6-6"/></svg></button>
                <button ref="next" type="button" class="carousel-arrow carousel-arrow--next" aria-label="Next"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="m9 18 6-6-6-6"/></svg></button>
              </div>
            </div>
            <div class="carousel-controls">
              <div class="carousel-dots" role="tablist" aria-label="Slide navigation"></div>
            </div>
          </carousel-embla>
        `;
        const wrapper = this.querySelector(".w-full");
        if (wrapper) {
          wrapper.insertAdjacentHTML("beforeend", carouselHTML);
          carousel = this.querySelector("carousel-embla");
        } else {
          return;
        }
      }
      const container = carousel.querySelector('[ref="container"]');
      if (!container) {
        return;
      }
      container.innerHTML = "";
      limitedProducts.forEach((product, index) => {
        const shouldLazyLoad = index >= 4;
        const cardHTML = this.#createProductCardFromJson(product, shouldLazyLoad);
        const slideHTML = `<div class="carousel-slide shrink-0">${cardHTML}</div>`;
        container.insertAdjacentHTML("beforeend", slideHTML);
      });
      if (carousel.embla) {
        carousel.embla.reInit();
      } else if (typeof carousel.connectedCallback === "function") {
        carousel.connectedCallback();
      }
    } else {
      const loadingGrid = this.querySelector(".grid");
      if (!loadingGrid) {
        return;
      }
      loadingGrid.innerHTML = "";
      limitedProducts.forEach((product, index) => {
        const shouldLazyLoad = index >= 4;
        const cardHTML = this.#createProductCardFromJson(product, shouldLazyLoad);
        loadingGrid.insertAdjacentHTML("beforeend", cardHTML);
      });
      loadingGrid.setAttribute(
        "data-has-recommendations",
        limitedProducts.length > 0 ? "true" : "false"
      );
    }
  }
  /**
   * Create product card HTML from JSON product data
   * @param {Object} product - Product object from JSON API
   * @param {boolean} lazyLoad - Whether to lazy load the image
   * @returns {string} HTML string
   */
  #createProductCardFromJson(product, lazyLoad = false) {
    const variant = product.variants?.[0] || product.variants?.[0];
    const image = product.featured_image || variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || product.images?.[0] || product.media?.[0]?.src || "";
    const imageUrl = image && typeof image === "string" ? image.startsWith("//") ? `https:${image}` : image : "";
    const price = variant?.price || product.price || 0;
    const comparePrice = variant?.compare_at_price || product.compare_at_price || null;
    const locale = window.Shopify?.locale || "de-DE";
    const currency = this.dataset.currency || "EUR";
    const priceFormatted = formatMoney(price, { locale, currency });
    const comparePriceFormatted = comparePrice ? formatMoney(comparePrice, { locale, currency }) : null;
    let savingsPercent = 0;
    if (comparePrice && comparePrice > price) {
      savingsPercent = Math.round((comparePrice - price) / comparePrice * 100);
    }
    const available = variant?.available !== false && product.available !== false;
    const hasSale = savingsPercent > 0;
    const hasTopseller = product.tags?.includes("Topseller") || product.tags?.includes("Besteseller") || product.tags?.includes("Bestseller");
    const t = window.Theme?.translations?.products || {};
    const soldOut = t.soldOut || "Ausverkauft";
    const onSale = t.onSale || "Sale";
    const discount = t.discount || "Rabatt";
    let badgesHTML = "";
    if (!available) {
      badgesHTML += `<span class="badge badge-warning" role="status"><span class="sr-only">Status: </span>${soldOut}</span>`;
    } else if (hasSale) {
      badgesHTML += `<span class="badge badge-error" role="status"><span class="sr-only">${discount}: </span>${onSale}</span>`;
    }
    if (hasTopseller) {
      badgesHTML += '<span class="badge badge-primary" role="status"><span class="sr-only">Status: </span>Topseller</span>';
    }
    const strikethroughClass = this.dataset.strikethroughPriceClass || "line-through text-gray-300 text-2xs font-bold leading-none";
    const salePriceClass = this.dataset.salePriceClass || "font-bold h6 leading-none";
    const regularPriceClass = this.dataset.regularPriceClass || "font-bold h6 leading-none";
    let priceHTML = "";
    if (comparePriceFormatted && hasSale) {
      priceHTML = `
        <div class="mt-2">
          <div class="flex items-end gap-x-2">
          <span class="${salePriceClass}">${priceFormatted}</span>
            <span class="${strikethroughClass}">${comparePriceFormatted}</span>
          </div>
        </div>
      `;
    } else {
      priceHTML = `
        <div class="mt-2">
          <span class="${regularPriceClass}">${priceFormatted}</span>
        </div>
      `;
    }
    return `
      <div class="product-card-wrapper hover:transform hover:-translate-y-2 transition-transform duration-200">
        <a href="${escapeHtml(product.url || `/products/${product.handle}`)}" class="group block" title="${escapeHtml(product.title)}">
          <div class="relative mb-3 overflow-hidden bg-gray-100 rounded-sm max-md:mb-2 max-h-[328px]">
            <div class="pointer-events-none absolute top-3 left-3 z-10 flex gap-2">
              ${badgesHTML}
            </div>
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" class="w-full h-full object-contain mix-blend-multiply" ${lazyLoad ? 'loading="lazy"' : ""} width="400" height="400">` : ""}
          </div>
          <span class="text-xs line-clamp-1">
            ${escapeHtml(product.title)}
          </span>
          ${priceHTML}
        </a>
      </div>
    `;
  }
  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Product recommendations error:", error.message);
    this.classList.add("hidden");
    this.dataset.error = "Error loading product recommendations";
  }
}
defineCustomElement("product-recommendations", ProductRecommendations);
